import React, { useState, useRef, useEffect } from 'react';
import { useWhatsAppIntegration } from '@/hooks/useWhatsAppIntegration';
import DevelopmentControls from './DevelopmentControls';

// Refactored components
import { Header } from './WhatsAppIntegration/Header';
import { ConnectionStatusCard } from '@/components/WhatsAppIntegration/ConnectionStatus';
import { ConnectionActions } from '@/components/WhatsAppIntegration/ConnectionActions';
import { MessageSender } from '@/components/WhatsAppIntegration/MessageSender';

// Modal components (simplified for now)
import { DisconnectModal } from '@/components/WhatsAppIntegration/DisconnectModal';
import { PhoneNumberInUseModal } from './WhatsAppIntegration/PhoneNumberInUseModal';
import { TemplatesTab } from '@/components/WhatsAppIntegration/TemplatesTab';
import { DataViewer } from './WhatsAppIntegration/DataViewer';
import { LogsViewer } from './WhatsAppIntegration/LogsViewer';

const WhatsAppIntegration: React.FC = () => {
  const {
    flowState,
    sessionData,
    connectionStatus,
    whatsappBusinessData,
    localStorageData,
    logs,
    sessionInfoResponse,
    sdkResponse,
    launchWhatsAppSignup,
    disconnect,
    retryConnection,
    clearLogs,
    cancelProcess,
    runCompleteWorkflow,
    getMessageTemplates,
    createMessageTemplate,
    sendTemplateMessage,
    clearMessageHistory,
    clearTemplateSendResponses,
    setDevelopmentState,
    showPhoneNumberInUseModal,
    phoneNumberInUse,
    handlePhoneNumberInUseModalDismiss
  } = useWhatsAppIntegration();

  // Modal states
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'messages' | 'templates'>('messages');
  
  // Ref for scrolling to tabs section
  const tabsSectionRef = useRef<HTMLDivElement>(null);

  const handleDisconnectRequest = () => {
    setShowDisconnectModal(true);
  };

  const handleDisconnectConfirm = () => {
    disconnect();
    setShowDisconnectModal(false);
  };

  const handleDisconnectCancel = () => {
    setShowDisconnectModal(false);
  };

  const isBusinessDataReady = (flowState.currentStep === 'business_onboarding_completed' || 
    (connectionStatus.isConnected && whatsappBusinessData.waba_id));
  
  const showTestData = process.env.ENVIRONMENT !== 'prod';

  // Auto-scroll to tabs when business data is ready and there's work in progress
  useEffect(() => {
    if (isBusinessDataReady && tabsSectionRef.current) {
      // Check if there's async work in progress (workflow loading, templates loading, etc.)
      const hasWorkInProgress = whatsappBusinessData.workflow_loading || 
                                whatsappBusinessData.templates_loading || 
                                whatsappBusinessData.creating_template ||
                                flowState.isLoading;

      if (hasWorkInProgress) {
        // Smooth scroll to tabs section with some delay to ensure rendering is complete
        setTimeout(() => {
          tabsSectionRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        }, 300);
      }
    }
  }, [isBusinessDataReady, whatsappBusinessData.workflow_loading, whatsappBusinessData.templates_loading, whatsappBusinessData.creating_template, flowState.isLoading]);

  return (
    <div className="min-h-screen bg-[#F7F6FD] py-12 px-4 sm:px-6 lg:px-8 relative">
      {/* Development Controls */}
      <DevelopmentControls
        onStateChange={setDevelopmentState}
        currentFlowState={flowState}
        currentWhatsappData={whatsappBusinessData}
      />
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <Header />

        {/* Connection Status */}
        <div className="mb-6">
          <ConnectionStatusCard
            flowState={flowState}
            connectionStatus={connectionStatus}
            onDisconnect={handleDisconnectRequest}
          />

          {/* Connection Actions */}
          <div className="mt-4">
            <ConnectionActions
              flowState={flowState}
              connectionStatus={connectionStatus}
              onConnect={launchWhatsAppSignup}
              onCancel={cancelProcess}
              onRetry={retryConnection}
            />
          </div>
        </div>

        {/* Business API Operations */}
        {isBusinessDataReady && (
          <div className="mb-6" ref={tabsSectionRef}>
            {/* Tabs */}
            <div className="bg-white rounded-lg border border-[#BCBCBC] shadow-[0_2px_6px_0_rgba(0,0,0,0.16)] p-6">
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  onClick={() => setActiveTab('messages')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${
                    activeTab === 'messages'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Enviar Mensajes
                </button>
                <button
                  onClick={() => setActiveTab('templates')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${
                    activeTab === 'templates'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Plantillas de Mensajes
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === 'messages' && (
                <MessageSender
                  whatsappBusinessData={whatsappBusinessData}
                  onSendMessage={runCompleteWorkflow}
                />
              )}

              {activeTab === 'templates' && (
                <TemplatesTab
                  whatsappBusinessData={whatsappBusinessData}
                  onGetTemplates={getMessageTemplates}
                  onCreateTemplate={createMessageTemplate}
                  onSendTemplate={sendTemplateMessage}
                  onClearResponses={clearTemplateSendResponses}
                />
              )}
            </div>
          </div>
        )}

        {/* Data Viewers */}
        { showTestData &&
          <>
            <DataViewer
              title="Datos en localStorage"
              data={localStorageData}
              hasData={!!localStorageData}
            />
    
            <DataViewer
              title="Datos de Sesión en Memoria"
              data={{
                sessionInfoResponse,
                sdkResponse,
                authResponse: sessionData.authResponse,
                sessionInfo: sessionData.sessionInfo,
                timestamp: sessionData.timestamp
              }}
              hasData={!!(sessionInfoResponse || sdkResponse || sessionData.authResponse)}
            />
    
            <LogsViewer
              logs={logs}
              onClearLogs={clearLogs}
            />
          </>
        }
      </div>

      {/* Disconnect Modal */}
      {showDisconnectModal && (
        <DisconnectModal
          onConfirm={handleDisconnectConfirm}
          onCancel={handleDisconnectCancel}
        />
      )}

      {/* Phone Number In Use Modal */}
      {showPhoneNumberInUseModal && (
        <PhoneNumberInUseModal
          onDismiss={handlePhoneNumberInUseModalDismiss}
          phoneNumber={phoneNumberInUse}
        />
      )}
    </div>
  );
};

export default WhatsAppIntegration;
export { WhatsAppIntegration };